import React from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import {
    withStyles,
    Button,
    CircularProgress,
    Typography,
    FormGroup,
    Paper,
    Slider,
    InputLabel,
    FormControl,
    TextField,
    Grid,
    Select,
    MenuItem,
} from '@material-ui/core';
import { connect } from 'react-redux';
import { BackButton, hasPermission } from '../../../../sc-utilities';
import * as diagnoseCaseActions from '../../../../sc-settings/CustomerDiagnoseCase/store/customerDiagnoseCaseActions';
import * as adminDiagnoseCaseActions from '../../../../sc-admin/AdminDiagnoseCase/store/adminDiagnoseCaseActions';
import History from '../../../../../History';

const styles = (theme) => ({
    root: {
        width: 'auto',
        height: 'auto',
        padding: theme.spacing(2),
        margin: theme.spacing(2),
    },
    form: {
        width: '95%',
        margin: theme.spacing(2),
        marginBottom: theme.spacing(2),
    },
    submit: {
        textTransform: 'none',
        color: theme.palette.buttonText.default,
        backgroundColor: theme.palette.primary.main,
        borderRadius: '1px',
        '&:hover': {
            backgroundColor: theme.palette.secondary.main,
        },
        width: '100%',
        marginTop: '10px',
    },
    formControl: {
        width: '100%',
    },
    inputLabel: {
        fontSize: '1.3rem',
    },
    slider: {
        paddingTop: '3rem',
    },
});

const FormFields = {
    battery_drain_mode: 'battery_drain_mode',
    minimum_battery_percentage_on_start: 'minimum_battery_percentage_on_start',
    minimum_battery_percentage_during_drain: 'minimum_battery_percentage_during_drain',
    drain_until_this_percentage_loss_occurred: 'drain_until_this_percentage_loss_occurred',
    minimum_allowed_time_until_the_percentage_loss_occurred: 'minimum_allowed_time_until_the_percentage_loss_occurred',
    drain_duration: 'drain_duration',
    maximum_amount_of_battery_loss_after_drain_duration: 'maximum_amount_of_battery_loss_after_drain_duration',
};

function valueText(value) {
    return `${value}%`;
}
const DeviceCaseSupportedDiagnoseDetailsBatteryDischarge = (props) => {
    const {
        classes,
        translations,
        permissions,
        onUpdateAdminDiagnoseCaseParams,
        onUpdateCustomerDiagnoseCaseParams,
        activeCustomerID,
    } = props;

    const diagnoseCase = props.location.state.redirectOptions?.rowData;

    const caseParams = diagnoseCase?.ThresholdParams || {};

    const isSystemDiagnoseCase = props.location.state?.redirectOptions?.isSystemDiagnoseCase ?? false;

    const batteryDrainModes = [
        { value: 'percentage', displayValue: translations['percentage'] || 'Percentage' },
        { value: 'duration', displayValue: translations['duration'] || 'Duration' },
        { value: 'full_discharge', displayValue: translations['full_discharge'] || 'Full discharge' },
    ];

    const initialValues = {
        battery_drain_mode: caseParams?.battery_drain_mode ?? 'percentage',
        minimum_battery_percentage_on_start: caseParams?.minimum_battery_percentage_on_start ?? 10,
        minimum_battery_percentage_during_drain: caseParams?.minimum_battery_percentage_during_drain ?? 1,
        drain_until_this_percentage_loss_occurred: caseParams?.drain_until_this_percentage_loss_occurred ?? 10,
        minimum_allowed_time_until_the_percentage_loss_occurred: caseParams?.minimum_allowed_time_until_the_percentage_loss_occurred ?? 10,
        drain_duration: caseParams?.drain_duration ?? 10,
        maximum_amount_of_battery_loss_after_drain_duration: caseParams?.maximum_amount_of_battery_loss_after_drain_duration ?? 10,
    };

    const hasDiagnoseCaseUpdatePermission = hasPermission(permissions, isSystemDiagnoseCase ? "diagnose_case_update" : "customer_diagnose_case_update");

    const formSettings = {
        Validation: {
            battery_drain_mode: Yup.mixed().oneOf(batteryDrainModes.map((drainMode) => drainMode.value)),
            minimum_battery_percentage_on_start: Yup.number(),
            minimum_battery_percentage_during_drain: Yup.number(),
            drain_until_this_percentage_loss_occurred: Yup.number(),
            minimum_allowed_time_until_the_percentage_loss_occurred: Yup.number(),
            drain_duration: Yup.number(),
            maximum_amount_of_battery_loss_after_drain_duration: Yup.number(),
        },
        FormTitle: translations['update_parameters'] || 'Update parameters',
        ButtonSubmit: hasDiagnoseCaseUpdatePermission
            ? {
                  Title: translations['save'] || 'SAVE',
              }
            : null,
    };

    const handleEdit = async (data) => {
        const CaseParams = {
            battery_drain_mode: data.battery_drain_mode,
            minimum_battery_percentage_on_start: data.minimum_battery_percentage_on_start,
            minimum_battery_percentage_during_drain: data.minimum_battery_percentage_during_drain,
            drain_until_this_percentage_loss_occurred: data.drain_until_this_percentage_loss_occurred,
            minimum_allowed_time_until_the_percentage_loss_occurred:
                data.minimum_allowed_time_until_the_percentage_loss_occurred,
            drain_duration: data.drain_duration,
            maximum_amount_of_battery_loss_after_drain_duration:
                data.maximum_amount_of_battery_loss_after_drain_duration,
        };

        isSystemDiagnoseCase
            ? await onUpdateAdminDiagnoseCaseParams({
                  CaseParams,
                  DiagnoseCaseID: diagnoseCase.DiagnoseCaseID,
              })
            : await onUpdateCustomerDiagnoseCaseParams({
                  CaseParams,
                  DiagnoseCaseID: diagnoseCase.DiagnoseCaseID,
                  ActiveCustomerID: activeCustomerID,
              });
        History.goBack();
    };
    return (
        <Paper className={classes.root}>
            {BackButton(translations, classes.customTooltip)}
            <Formik
                initialValues={initialValues}
                enableReinitialize
                onSubmit={handleEdit}
                validationSchema={Yup.object().shape(formSettings.Validation)}
            >
                {(formikProps) => {
                    const { values, isSubmitting, isValid, dirty, handleSubmit, handleChange, setFieldValue } =
                        formikProps;

                    return (
                        <form className={classes.form} onSubmit={handleSubmit}>
                            <>
                                <Typography align="center" variant={'h5'}>
                                    {translations['battery_discharge'] || 'Battery Discharge'}
                                </Typography>
                                <InputLabel shrink className={classes.inputLabel}>
                                    {translations['battery_drain_mode'] || 'Battery Drain Mode'}
                                </InputLabel>
                                <FormGroup>
                                    <FormControl className={classes.formControl} margin={'dense'}>
                                        <Select
                                            name={FormFields.battery_drain_mode}
                                            label={translations['battery_drain_mode'] || 'Battery Drain Mode'}
                                            margin={'dense'}
                                            value={values.battery_drain_mode || initialValues.battery_drain_mode}
                                            onChange={handleChange}
                                        >
                                            {(batteryDrainModes ?? []).map((option) => (
                                                <MenuItem key={option.value} value={option.value}>
                                                    {option.displayValue}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </FormGroup>
                                <InputLabel shrink className={classes.inputLabel}>
                                    {translations['minimum_battery_percentage_on_start'] ||
                                        'Minimum battery percentage on start'}
                                </InputLabel>
                                <FormGroup>
                                    <FormControl className={classes.formControl} margin={'dense'}>
                                        <Slider
                                            className={classes.slider}
                                            value={
                                                values.minimum_battery_percentage_on_start ||
                                                initialValues.minimum_battery_percentage_on_start
                                            }
                                            valueLabelFormat={valueText}
                                            aria-label={
                                                translations['minimum_battery_percentage_on_start'] ||
                                                'Minimum battery percentage on start'
                                            }
                                            valueLabelDisplay="on"
                                            onChange={(_, value) =>
                                                setFieldValue('minimum_battery_percentage_on_start', value)
                                            }
                                        />
                                    </FormControl>
                                </FormGroup>
                                <InputLabel shrink className={classes.inputLabel}>
                                    {translations['minimum_battery_percentage_during_drain'] ||
                                        'Minimum battery percentage during drain'}
                                </InputLabel>
                                <FormGroup>
                                    <FormControl className={classes.formControl} margin={'dense'}>
                                        <Slider
                                            className={classes.slider}
                                            value={
                                                values.minimum_battery_percentage_during_drain ||
                                                initialValues.minimum_battery_percentage_during_drain
                                            }
                                            valueLabelFormat={valueText}
                                            aria-label={
                                                translations['minimum_battery_percentage_during_drain'] ||
                                                'Minimum battery percentage during drain'
                                            }
                                            valueLabelDisplay="on"
                                            onChange={(_, value) =>
                                                setFieldValue('minimum_battery_percentage_during_drain', value)
                                            }
                                        />
                                    </FormControl>
                                </FormGroup>
                                <InputLabel shrink className={classes.inputLabel}>
                                    {translations['drain_until_this_percentage_loss_occurred'] ||
                                        'Drain until this percentage loss occurred'}
                                </InputLabel>
                                <FormGroup>
                                    <FormControl className={classes.formControl} margin={'dense'}>
                                        <Slider
                                            className={classes.slider}
                                            value={
                                                values.drain_until_this_percentage_loss_occurred ||
                                                initialValues.drain_until_this_percentage_loss_occurred
                                            }
                                            valueLabelFormat={valueText}
                                            aria-label={
                                                translations['drain_until_this_percentage_loss_occurred'] ||
                                                'Drain until this percentage loss occurred'
                                            }
                                            valueLabelDisplay="on"
                                            onChange={(_, value) =>
                                                setFieldValue('drain_until_this_percentage_loss_occurred', value)
                                            }
                                        />
                                    </FormControl>
                                </FormGroup>
                                <InputLabel shrink className={classes.inputLabel}>
                                    {translations['minimum_allowed_time_until_the_percentage_loss_occurred'] ||
                                        'Minimum allowed time until the percentage loss occurred (minutes)'}
                                </InputLabel>
                                <Grid item xs={12} sm={6}>
                                    <FormGroup>
                                        <FormControl className={classes.formControl} margin={'dense'}>
                                            <TextField
                                                name={
                                                    FormFields.minimum_allowed_time_until_the_percentage_loss_occurred
                                                }
                                                value={values.minimum_allowed_time_until_the_percentage_loss_occurred}
                                                aria-label={`${
                                                    translations[
                                                        'minimum_allowed_time_until_the_percentage_loss_occurred'
                                                    ] ||
                                                    'Minimum allowed time until the percentage loss occurred (minutes)'
                                                }`}
                                                margin={'dense'}
                                                fullWidth
                                                type={'number'}
                                                onChange={handleChange}
                                                InputProps={{ inputProps: { min: 0 } }}
                                            />
                                        </FormControl>
                                    </FormGroup>
                                </Grid>
                                <InputLabel shrink className={classes.inputLabel}>
                                    {translations['drain_duration'] || 'Drain duration (minutes)'}
                                </InputLabel>
                                <Grid item xs={12} sm={6}>
                                    <FormGroup>
                                        <FormControl className={classes.formControl} margin={'dense'}>
                                            <TextField
                                                name={FormFields.drain_duration}
                                                value={values.drain_duration}
                                                aria-label={`${
                                                    translations['drain_duration'] || 'Drain duration (minutes)'
                                                }`}
                                                margin={'dense'}
                                                fullWidth
                                                type={'number'}
                                                onChange={handleChange}
                                                InputProps={{ inputProps: { min: 0 } }}
                                            />
                                        </FormControl>
                                    </FormGroup>
                                </Grid>
                                <InputLabel shrink className={classes.inputLabel}>
                                    {translations['maximum_amount_of_battery_loss_after_drain_duration'] ||
                                        'Maximum amount of battery loss after drain duration'}
                                </InputLabel>
                                <FormGroup>
                                    <FormControl className={classes.formControl} margin={'dense'}>
                                        <Slider
                                            className={classes.slider}
                                            value={
                                                values.maximum_amount_of_battery_loss_after_drain_duration ||
                                                initialValues.maximum_amount_of_battery_loss_after_drain_duration
                                            }
                                            valueLabelFormat={valueText}
                                            aria-label={
                                                translations['maximum_amount_of_battery_loss_after_drain_duration'] ||
                                                'Maximum amount of battery loss after drain duration'
                                            }
                                            valueLabelDisplay="on"
                                            onChange={(_, value) =>
                                                setFieldValue(
                                                    'maximum_amount_of_battery_loss_after_drain_duration',
                                                    value
                                                )
                                            }
                                        />
                                    </FormControl>
                                </FormGroup>
                                <div>
                                    {formSettings.ButtonSubmit ? (
                                        <Button
                                            fullWidth={true}
                                            className={classes.submit}
                                            variant="contained"
                                            color={formSettings.ButtonSubmit.Color}
                                            type="submit"
                                            style={{ color: isSubmitting || !(isValid && dirty) ? null : 'white' }}
                                        >
                                            {isSubmitting ? (
                                                <CircularProgress size={24} />
                                            ) : (
                                                formSettings.ButtonSubmit.Title
                                            )}
                                        </Button>
                                    ) : null}
                                </div>
                            </>
                        </form>
                    );
                }}
            </Formik>
        </Paper>
    );
};
const mapStateToProps = (state) => {
    return {
        translations:
            state.translate && state.translate.translations
                ? state.translate.translations.activeTranslations || {}
                : {},
        permissions: state?.core?.login?.permissions || [],
        activeCustomerID: state?.core?.login?.activeCustomer?.CustomerID ?? null,
    };
};
const mapDispatchToProps = (dispatch) => {
    return {
        onUpdateAdminDiagnoseCaseParams: (params) =>
            dispatch(adminDiagnoseCaseActions.updateAdminDiagnoseCaseParams(params)),
        onUpdateCustomerDiagnoseCaseParams: (params) =>
            dispatch(diagnoseCaseActions.updateCustomerDiagnoseCaseParams(params)),
    };
};
export default connect(
    mapStateToProps,
    mapDispatchToProps
)(withStyles(styles)(DeviceCaseSupportedDiagnoseDetailsBatteryDischarge));
